import uuid from "uuid";

import {getBaseDomainUrl} from "../../../api/jsonApi";
import {convertImgToBase64URLNoCors} from "../../../misc/utils";
import {setAILoading, setAIResults, setLookAIEditPiece} from "../aiActions";

// "Regenerate" of the custom prompt block (AIResultCustomPrompt).
//
// It has nothing to do with the /start-convert + poll flow of aiApi.js: the backend exposes a
// separate, synchronous endpoint for arbitrary "photo + free-text prompt" edits, so here we
// just post the image the user is editing (aiState.lookAIEditPiece.dataUrl - a cut piece of a
// multiImage result, or the whole result image) together with their typed prompt
// (aiState.lookAICustomPrompt) and swap the returned image back in - what is on screen is
// re-rendered with the new image, the rest of the result is untouched.
//
// The regenerated image is pushed into aiResults as its own entry and takes over isActive
// (every other entry is deactivated), so the overlay switches to it and shows the before/after
// slider. Its id differs from lookAIEditPiece.resultId, so LookAIImageOverlayImageContent drops
// the edit mode on its own (the "switching to another result" effect - it also unlocks the
// image area height). Keeping every regenerate as its own entry means none of them is lost.
//
// POST /api/ImageProcessing/edit-image
//   { prompt, b64Image, fileName?, model?, imageSize?, applyWatermark?, fabricatorId? }
// 200 -> { resultImageUrl, prompt, model, conversionError }

const newSwaggerHeaders = {
  "Accept": "*",
  "Content-Type": "application/json",
};

type EditImageRequest = {
  prompt: string,
  b64Image: string,
  fileName?: string,
  model?: string,
  imageSize?: "1K" | "2K" | "4K", // gemini only
  applyWatermark?: boolean,
  fabricatorId?: string | number,
};

type EditImageResponse = {
  resultImageUrl?: string,
  prompt?: string,
  model?: string,
  conversionError?: string | null,
};

// the endpoint only reads the extension of fileName (jpg/png/heic/heif/avif/webp/bmp),
// so it is derived from the image itself - a cut piece is always a png (see aiCutImageIntoPieces),
// a result image or an already regenerated one is whatever the backend returned (usually .jpg).
const _getFileName = (imageUrl: string): string => {
  const dataUrlMatch = /^data:image\/([a-z0-9+.-]+);/i.exec(imageUrl || "");
  if (dataUrlMatch) {
    return `image.${dataUrlMatch[1].toLowerCase()}`;
  }
  const extensionMatch = /\.(jpe?g|png|heic|heif|avif|webp|bmp)(?:[?#]|$)/i.exec(imageUrl || "");
  return extensionMatch ? `image.${extensionMatch[1].toLowerCase()}` : "image.png";
};

// lookAIEditPiece.dataUrl is a base64 data url right after the cut, but an s3 url for a
// whole-image edit and once the image has already been regenerated once - so fetch it back as
// base64 in those cases.
const _toBase64Image = async (imageUrl: string): Promise<string> => {
  if (!imageUrl) {
    return "";
  }
  if (imageUrl.indexOf("data:") === 0) {
    return imageUrl;
  }
  return ((await convertImgToBase64URLNoCors(imageUrl)) as string) || "";
};

const _getErrorMessage = async (errOrResponse: any): Promise<string> => {
  let message = "Failed to regenerate image. Please try again later";
  if (errOrResponse && typeof errOrResponse === "object" && "ok" in errOrResponse && "status" in errOrResponse) {
    try {
      const contentType = errOrResponse.headers?.get("content-type") || "";
      if (contentType.indexOf("application/json") > -1) {
        const data = await errOrResponse.json();
        message = data?.message || JSON.stringify(data);
      } else {
        message = (await errOrResponse.text()) || message;
      }
    } catch (e) {
      // keep the fallback message
    }
    return `HTTP ${errOrResponse.status}: ${message}`;
  }
  message = errOrResponse?.message || String(errOrResponse) || message;
  if (message === "signal is aborted without reason") {
    message = "Aborted manually";
  }
  return message;
};

// Same slot rule as _parseImageResponse in aiApi.js: the results list starts out with empty
// placeholder slots (see aiReducer initialState), so a new entry fills the first one that has
// no image yet and is only appended once every slot is taken.
// When the new entry is active, every other entry is deactivated - the overlay picks the FIRST
// isActive result, so leaving the edited one active would keep it (and the edit mode) on screen.
const _appendRegeneratedResult = (aiResults: any[], entry: any): any[] => {
  const nextAiResults = (aiResults || []).map(aiResult => Object.assign({}, aiResult,
    entry.isActive ? {isActive: false} : null));
  const nextUnrenderedIndex = nextAiResults.findIndex(aiResult => !aiResult.resultImageUrl);
  if (nextUnrenderedIndex > -1) {
    nextAiResults[nextUnrenderedIndex] = entry;
    return nextAiResults;
  }
  return [...nextAiResults, entry];
};

// The "before" image of a regenerated entry: the result being edited was itself generated from
// the user photo (aiResult.sourceImageUrl, see _parseImageResponse in aiApi.js), and that photo
// - not the image we posted - is what the before/after slider has to compare against.
// Falls back the same way LookAIImageOverlayImageContent does (aiUserFileBlobUrl), and only as
// a last resort to the posted piece itself.
const _getEditedResultSourceImageUrl = (aiResults: any[], lookAIEditPiece: any, aiUserFileBlobUrl: string): string => {
  const editedResult = (aiResults || []).find(aiResult => aiResult.id === lookAIEditPiece.resultId);
  return (editedResult && editedResult.sourceImageUrl) || aiUserFileBlobUrl || lookAIEditPiece.dataUrl;
};

export const postEditImage = async (postData: EditImageRequest, signal?: AbortSignal): Promise<EditImageResponse> => {
  const rawResponse = await fetch(`${getBaseDomainUrl()}:7122/api/ImageProcessing/edit-image`, {
    method: "POST",
    headers: newSwaggerHeaders,
    body: JSON.stringify(postData),
    signal,
  });
  if (!rawResponse.ok) {
    throw await _getErrorMessage(rawResponse);
  }
  return await rawResponse.json();
};

// Thunk used by the "regenerate" button. Keeps aiRegenerateLoading on for the whole round trip
// (see setAILoading(true, true)) - that is what disables the textarea/button and shows the
// loading bar over the zoomed piece.
export const postRegenerateLookAiImage = (signal?: AbortSignal) => {
  return async (dispatch: any, getState: any): Promise<string> => {
    const {lookAICustomPrompt, lookAIEditPiece, aiRenderOptions} = getState().aiState;

    if (!lookAIEditPiece || !lookAIEditPiece.dataUrl || !lookAICustomPrompt || !lookAICustomPrompt.trim()) {
      return "";
    }

    dispatch(setAILoading(true, true));

    try {
      const b64Image = await _toBase64Image(lookAIEditPiece.dataUrl);
      if (!b64Image) {
        throw new Error("Could not read the image being edited");
      }

      const {selectedImageModel, customImageModel} = aiRenderOptions || {};
      const model = selectedImageModel === "custom" ? customImageModel : selectedImageModel;

      const postData: EditImageRequest = {
        prompt: lookAICustomPrompt.trim(),
        b64Image, // the "data:image/...;base64," prefix is stripped by the backend
        fileName: _getFileName(lookAIEditPiece.dataUrl),
        fabricatorId: document.kvConfig.userId, // only used to name the s3 object
      };
      if (model) {
        postData.model = model;
      }

      const editResponse = await postEditImage(postData, signal);

      if (!editResponse || !editResponse.resultImageUrl) {
        throw new Error(editResponse?.conversionError || "Empty response");
      }

      // the regenerated image becomes its own, active entry of the results list (same idea as
      // _parseImageResponse in aiApi.js, which gives every finished conversion its own slot).
      // lookAIEditPiece is deliberately not touched here: it still points at the edited result,
      // which is no longer active, so the overlay leaves the edit mode by itself.
      const {aiResults: currentAiResults, aiUserFileBlobUrl} = getState().aiState;
      dispatch(setAIResults(_appendRegeneratedResult(currentAiResults, {
        ...editResponse,
        id: uuid.v4(),
        isActive: true, // switches the overlay to it - before/after slider, edit mode off
        // the "before" of this entry is the user photo the edited result was generated from -
        // not the image we posted, which is itself a generated image (lookAIEditPiece.resultId
        // keeps pointing at that original result across regenerates)
        sourceImageUrl: _getEditedResultSourceImageUrl(currentAiResults, lookAIEditPiece, aiUserFileBlobUrl),
        prompt: editResponse.prompt || lookAICustomPrompt.trim(),
        isRegenerated: true,
      })));

      dispatch(setAILoading(false));
      return editResponse.resultImageUrl;
    } catch (e) {
      const message = typeof e === "string" ? e : await _getErrorMessage(e);
      console.log("postRegenerateLookAiImage error", message);
      // the error is kept on the edit target itself (the prompt block shows it) - the generated
      // results list is not touched, the user can simply retype and try again
      const {lookAIEditPiece: currentEditPiece} = getState().aiState;
      if (currentEditPiece) {
        dispatch(setLookAIEditPiece(Object.assign({}, currentEditPiece, {regenerateError: message})));
      }
      dispatch(setAILoading(false));
      return "";
    }
  };
};
